import moment from 'moment';
import i18n from 'i18n';
import { getDayOfWeekLabelByValue } from 'components/Dayparts/Dayparts';
import { SpaceHealthState } from 'enum/SpaceHealthState';
import { DeviceFootTrafficPoint } from 'core/deviceManagement/DeviceManagement';
import { numberWithCommas } from 'utils/StringUtil';

/** 沒有值時顯示的字樣。空字串會讓整格塌掉，看起來像漏畫。 */
export const NO_VALUE = '—';

/**
 * 沒有來源原因可用的那幾個狀態，各自的固定敘述。
 *
 * 停止服務不在這份對照裡：它的說明逐筆由來源給。
 */
const STATUS_NOTE_I18N: Partial<Record<SpaceHealthState, string>> = {
  [SpaceHealthState.WARMING_UP]: 'spaceHealth.descriptions.warmingUp',
  [SpaceHealthState.NO_DATA]: 'spaceHealth.descriptions.noData'
};

/**
 * 狀態說明的內容；沒有東西可寫時回 undefined，整個區塊就不顯示。
 *
 * 三種狀態的內容來源刻意不同：停止服務是人為異動，上游會逐筆帶下異動原因
 * （SSP 的 spaceStatusModifyReason），那句話比任何固定敘述都具體，所以照抄；
 * 服務啟用中與訊號異常沒有對應的來源欄位，而它們的成因本來就只有一種，
 * 用固定敘述不會失真。狀態正常沒有說明可寫。
 *
 * 停止服務而來源沒給原因時一樣不顯示 —— 留一塊只有標題的區塊看起來像漏畫。
 *
 * 這裡刻意不補一份寫死的處理建議：上游的狀態資料只有狀態、異動原因與異動時間，
 * 沒有任何「該怎麼處理」的欄位，寫死的維運指示會隨作業流程改變而失真，而失真時
 * 不會有任何東西報錯。
 */
export function resolveStatusNote(
  state: SpaceHealthState,
  healthStateReason?: string
): string | undefined {
  if (state === SpaceHealthState.BANNED) {
    return healthStateReason?.trim() || undefined;
  }
  const key = STATUS_NOTE_I18N[state];
  return key ? (i18n.t(key) as string) : undefined;
}

/**
 * 人流讀數的顯示。
 *
 * `null` 代表「沒有讀數」，與「讀數是 0」意思相反：人流資料源目前不可信，
 * 後端寧可回 null 也不回 0。折成 0 會讓畫面宣稱「這個地點沒有人經過」，
 * 那是一句假話，而且看不出是假的。真的量到 0 才顯示 0。
 */
export function formatFootTraffic(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return NO_VALUE;
  }
  return value.toLocaleString();
}

/** 七天裡有沒有任何一天真的有讀數；全是 null 就不畫圖，改顯示空狀態。 */
export function hasFootTrafficReadings(
  points: DeviceFootTrafficPoint[]
): boolean {
  return points.some((point) => point.footfall !== null);
}

/**
 * 長條色。七天同一個顏色，不特別標任何一根。
 *
 * 設計稿把最後一根畫深，因為它那份假資料的最後一根是今天；而這裡的區間是不含今天的
 * 前七個完整日（今天還在同步中，納進來整天都在飄），最後一根是昨天。深色會讀成
 * 「這一根是今天」，而那句話不成立。
 */
export const FOOT_TRAFFIC_BAR_COLOR = '#068d9d';

/**
 * x 軸的刻度。
 *
 * 顯示星期而不是日期：這張圖問的是「這個點位一週內哪幾天人多」，而人流的週期
 * 是星期，不是日期。星期的字沿用時段設定那一套，同一個詞不會在兩處長得不一樣。
 *
 * 解析不出來的日期照原樣顯示 —— 換成空字串會讓那一格看起來像漏畫，寫死
 * 「Invalid date」更糟。
 */
function footTrafficAxisLabel(date: string): string {
  const day = moment(date);
  return day.isValid() ? getDayOfWeekLabelByValue(day.isoWeekday()) : date;
}

/**
 * 七天裡人流最高的那一天，寫成「週三 最高 1,234 人」。
 *
 * 長條圖只給得出相對高低，讀不出絕對量級 —— 這一句補的就是那個量級，同時把最高的是
 * 星期幾說出來，那是這張圖真正要回答的問題（哪幾天人多）。
 *
 * 沒有讀數的日子不參與比較；七天都讀不到就回 undefined，呼叫端整句不顯示。
 * 並列最高時取較早的那一天 —— 任意但固定，總比每次 render 換一天好。
 */
export function buildFootTrafficPeakLabel(
  points: DeviceFootTrafficPoint[]
): string | undefined {
  const readings = points.filter(
    (point): point is DeviceFootTrafficPoint & { footfall: number } =>
      point.footfall !== null
  );
  if (readings.length === 0) {
    return undefined;
  }
  const peak = readings.reduce((best, point) =>
    point.footfall > best.footfall ? point : best
  );
  return i18n.t('deviceManagement.labels.weeklyFootTrafficPeak', {
    day: footTrafficAxisLabel(peak.date),
    value: numberWithCommas(peak.footfall)
  }) as string;
}

/**
 * 長條圖的資料。沒有讀數的那一天送 null 給 chart.js，它會跳過那根長條；
 * 送 0 的話會畫出一根貼地的長條，看起來像「那天量到 0」。
 */
export function toFootTrafficChartData(points: DeviceFootTrafficPoint[]): {
  labels: string[];
  values: (number | null)[];
} {
  return {
    labels: points.map((point) => footTrafficAxisLabel(point.date)),
    values: points.map((point) => point.footfall)
  };
}

/**
 * 解析度。這是渠道層的設定，同一個渠道底下每台機器看到的都是同一組值，
 * 這是已知的限制，不是資料錯了。
 */
export function formatResolution(
  screenPixelWidth?: number,
  screenPixelHeight?: number
): string {
  if (screenPixelWidth === undefined || screenPixelHeight === undefined) {
    return NO_VALUE;
  }
  return `${screenPixelWidth} x ${screenPixelHeight}`;
}

/** 經緯度；缺任何一邊都當作沒有座標。 */
export function formatCoordinates(
  latitude?: number,
  longitude?: number
): string {
  if (latitude === undefined || longitude === undefined) {
    return NO_VALUE;
  }
  return `${latitude}, ${longitude}`;
}
